import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

/**
 * CodeEntryLanding - FaceTime incoming call style
 * Ultra-minimal entry point for friends joining via code
 * Zero friction, no account required, instant help
 */
const CodeEntryLanding = () => {
  const [code, setCode] = useState('');
  const [joining, setJoining] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [error, setError] = useState(null);


  // Auto-detect code from URL parameter and auto-join
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const codeParam = urlParams.get('code');

    // Free-tier class codes are 6-char alphanumeric (A-Z excluding O/I,
    // digits excluding 0/1). If a teacher shared a /join?code=XXXXXX link,
    // hand off to the class-code join flow instead of the 4-digit study
    // code flow this component was built for.
    if (codeParam && /^[A-Z2-9]{6}$/i.test(codeParam)) {
      const preserved = urlParams.toString();
      window.location.replace(`/class-join?${preserved}`);
      return;
    }

    if (codeParam && codeParam.length === 4) {
      setCode(codeParam);
      joinWithCode(codeParam);
    } else {
      // Auto-focus code input if no code in URL
      const input = document.getElementById('code-input');
      if (input) input.focus();
    }
  }, []);

  // Handle code input with auto-submit on 4 digits
  const handleCodeInput = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only digits
    setCode(value);
    setError(null);

    // Auto-submit when 4 digits entered
    if (value.length === 4) {
      joinWithCode(value);
    }
  };

  // Join study session with code
  const joinWithCode = async (inputCode) => {
    if (inputCode.length !== 4 || joining) return;

    setJoining(true);
    setError(null);

    try {
      const response = await fetch('/api/join-study-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: inputCode,
          friend_name: 'Friend'
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSessionData(data.session_info);


        // Redirect to friend collaboration interface with secure token
        setTimeout(() => {
          window.location.href = `/join/${data.session_token}`;
        }, 1500);
      } else {
        setError(data.error || 'Code not found. Check with your friend!');
        setCode('');
        setJoining(false);
      }
    } catch (error) {
      setError('Could not connect. Check your internet and try again.');
      setCode('');
      setJoining(false);
    }
  };

  // Show success/joining state
  if (sessionData) {
    return (
      <motion.div
        className="code-success"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #10B981 0%, #0EA5E9 100%)',
          color: 'white',
          padding: '20px',
          textAlign: 'center'
        }}
      >
        <motion.div
          className="success-animation"
          animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          🤝
        </motion.div>

        <h1 style={{ fontSize: '32px', marginBottom: '16px', fontWeight: '700' }}>
          Joining {sessionData.student_name}'s session!
        </h1>

        <p style={{ fontSize: '18px', opacity: 0.9, marginBottom: '8px' }}>
          Subject: {sessionData.subject}
        </p>

        <p style={{ fontSize: '16px', opacity: 0.8 }}>
          Connecting to study room...
        </p>

        <motion.div
          className="loading-dots"
          style={{ marginTop: '24px', display: 'flex', gap: '8px' }}
        >
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              style={{
                width: '8px',
                height: '8px',
                backgroundColor: 'white',
                borderRadius: '50%',
                opacity: 0.6
              }}
              animate={{ opacity: [0.6, 1, 0.6] }}
              transition={{
                repeat: Infinity,
                duration: 1.5,
                delay: i * 0.2
              }}
            />
          ))}
        </motion.div>
      </motion.div>
    );
  }

  // Main code entry interface
  return (
    <motion.div
      className="code-entry-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #FAFAFA 0%, #F1F5F9 100%)',
        padding: '20px'
      }}
    >
      <motion.div
        className="entry-container"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        style={{
          textAlign: 'center',
          maxWidth: '400px',
          width: '100%'
        }}
      >
        {/* Hero section */}
        <motion.div
          className="hero-emoji"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ repeat: Infinity, duration: 3 }}
          style={{ fontSize: '80px', marginBottom: '24px' }}
        >
          🤝
        </motion.div>

        <h1 style={{
          fontSize: '36px',
          fontWeight: '700',
          color: '#0F172A',
          marginBottom: '16px'
        }}>
          Help a Friend Study
        </h1>

        <p style={{
          fontSize: '18px',
          color: '#64748B',
          marginBottom: '40px',
          lineHeight: '1.5'
        }}>
          Enter the 4-digit code they shared with you
        </p>

        {/* Code input */}
        <motion.div
          className="code-input-container"
          style={{ marginBottom: '24px' }}
        >
          <input
            id="code-input"
            type="text"
            placeholder="0000"
            maxLength="4"
            pattern="[0-9]*"
            inputMode="numeric"
            value={code}
            onChange={handleCodeInput}
            disabled={joining}
            style={{
              width: '200px',
              height: '80px',
              fontSize: '32px',
              fontWeight: '800',
              fontFamily: 'SF Mono, Monaco, monospace',
              textAlign: 'center',
              letterSpacing: '8px',
              border: '3px solid #E2E8F0',
              borderRadius: '16px',
              background: 'white',
              color: '#0F172A',
              marginBottom: '16px',
              transition: 'all 0.2s',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#10B981';
              e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.2)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#E2E8F0';
              e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
            }}
          />

          <motion.button
            onClick={() => joinWithCode(code)}
            disabled={code.length !== 4 || joining}
            whileHover={code.length === 4 && !joining ? { scale: 1.05 } : {}}
            whileTap={code.length === 4 && !joining ? { scale: 0.95 } : {}}
            style={{
              width: '200px',
              height: '56px',
              background: code.length === 4 && !joining
                ? 'linear-gradient(135deg, #10B981 0%, #0EA5E9 100%)'
                : '#E2E8F0',
              border: 'none',
              borderRadius: '12px',
              color: code.length === 4 && !joining ? 'white' : '#94A3B8',
              fontSize: '18px',
              fontWeight: '600',
              cursor: code.length === 4 && !joining ? 'pointer' : 'default',
              transition: 'all 0.2s',
              boxShadow: code.length === 4 && !joining
                ? '0 4px 12px rgba(16, 185, 129, 0.3)'
                : 'none'
            }}
          >
            {joining ? 'Joining...' : 'Help Now'}
          </motion.button>
        </motion.div>

        {/* Error message */}
        {error && (
          <motion.div
            className="error-message"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              background: 'rgba(239, 68, 68, 0.1)',
              color: '#EF4444',
              padding: '12px 16px',
              borderRadius: '8px',
              fontSize: '14px',
              marginBottom: '20px',
              border: '1px solid rgba(239, 68, 68, 0.2)'
            }}
          >
            {error}
          </motion.div>
        )}

        {/* Helper text */}
        <p style={{
          fontSize: '16px',
          color: '#94A3B8',
          lineHeight: '1.5'
        }}>
          No account needed - just enter the code and start helping!
        </p>

        {/* Optional: Create account link */}
        <motion.button
          onClick={() => window.location.href = '/login'}
          whileHover={{ scale: 1.02 }}
          style={{
            background: 'none',
            border: 'none',
            color: '#10B981',
            fontSize: '14px',
            cursor: 'pointer',
            marginTop: '20px',
            textDecoration: 'underline'
          }}
        >
          Have an account? Sign in
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

export default CodeEntryLanding;